import { useLazyQuery, useQuery } from '@apollo/client';
import GetAppIcon from '@mui/icons-material/GetApp';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  INDUSTRY_TRACKER_TYPES,
  SECURE_REGION_DOWNLOAD_QUERY,
} from '@/components/Downloads/operations';
import OilRigIcon from '@/components/Icons/OilRigIcon';
import Modal from '@/components/Modals';
import { ShinyLink } from '@/components/Shiny';
import { useCurrentUser } from '@/contexts/CurrentUserContext';
import SnackbarContext from '@/contexts/SnackbarContext';
import { useGetFeatureFlags } from '@/hooks/useGetFeatureFlags';
import { MaOpportunitiesLinks } from '@/pages/LandingPage/LandingPageItems/DataDownloads/MaOpportunitiesLinks';
import { IndustryTrackerTypeEnum } from '@/types/GraphqlSchema';
import { defaultDimensions } from '@/utils/analytics';
import { getUserId } from '@/utils/auth';
import {
  ANALYTICS_EVENT_ACTIONS,
  ANALYTICS_EVENTS,
  DATA_DOWNLOADS_PATH,
  GLOBAL_DOWNLOADS_PATH,
} from '@/utils/constants';
import { INDUSTRY_TRACKER_IMAGES } from '../../constants';

const useStyles = makeStyles(theme => ({
  button: {
    ...theme.palette.button,

    // Not sure why it's necessary to override the disabled styles here,
    // as everything seems to be the same as other buttons
    // but the OilRigIcon color was not changing when disabled
    '&:disabled path': {
      fill: 'rgba(0, 0, 0, 0.2)',
    },
  },
  centeredButton: {
    ...theme.palette.button,
  },
  modal: {
    height: 'max-content',
    width: '70%', // override default modal width defined in Modals/index.js
    maxHeight: '90%',
  },
  modalButtons: {
    margin: '15px 0px 30px',
  },
  modalContent: {
    display: 'flex',
    paddingLeft: '80px',
    paddingRight: '80px',
    overflow: 'auto',
    margin: 'unset',
    width: 'unset',
  },
  modalGridItem: {
    minWidth: '190px',
  },
  updated: {
    ...theme.palette.accentText,
  },
}));

export default () => {
  const classes = useStyles();
  const { setSnack } = useContext(SnackbarContext);
  const { isUserEntitled } = useCurrentUser();
  const { inDevelopment, globalDownloads } = useGetFeatureFlags();

  const [open, setOpen] = useState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const { data: industryTrackerDownloadTypesData } = useQuery(INDUSTRY_TRACKER_TYPES, {
    fetchPolicy: 'cache-and-network',
  });
  const industryTrackerDownloadTypes =
    industryTrackerDownloadTypesData?.industryTrackerDownloadTypes;

  const downloadQueryOptions = useCallback(
    dataKey => ({
      fetchPolicy: 'network-only',
      onCompleted: data => {
        const { url, downloadMessage } = data[dataKey];
        if (url) {
          setSnack({
            open: true,
            message: `Your download should begin shortly.${downloadMessage}`,
            severity: 'success',
          });
          window.location.href = url;
        }
      },
      onError: error => {
        setSnack({
          severity: 'error',
          message: error.message,
          open: true,
        });
      },
    }),
    [setSnack],
  );

  const [getSecureRegionDownload] = useLazyQuery(
    SECURE_REGION_DOWNLOAD_QUERY,
    downloadQueryOptions('secureRegionDownload'),
  );

  const getDownloadLink = (downloadType, region, subType) => {
    getSecureRegionDownload({
      variables: { downloadType, region, subType },
    });
  };

  return (
    <Grid container spacing={2} size={12}>
      <Grid size={2} />
      <Grid container spacing={2} size={8}>
        <Grid>
          <ShinyLink
            startAdornment={<GetAppIcon fontSize='small' />}
            link={{ title: 'view downloads', page: 'download' }}
            styles={{ link: classes.button }}
            disabled={!isUserEntitled(DATA_DOWNLOADS_PATH)}
            variant='contained'
          />
        </Grid>
        <Grid>
          <Button
            onClick={handleOpen}
            variant='contained'
            className={classes.button}
            disabled={!isUserEntitled(DATA_DOWNLOADS_PATH)}
          >
            <OilRigIcon fontSize='small' />
            &nbsp;industry trackers
          </Button>
          <Modal
            title='Access the most up-to-date industry trackers.'
            open={open}
            handleClose={handleClose}
            styles={classes}
          >
            <Grid
              container
              spacing={5}
              className={classes.modalContent}
              sx={{ justifyContent: 'space-between' }}
            >
              <MaOpportunitiesLinks />
              {industryTrackerDownloadTypes?.map(({ name, type, regions, userEntitledRegions }) => {
                const image = INDUSTRY_TRACKER_IMAGES[type];

                if (!inDevelopment && type === IndustryTrackerTypeEnum.LngFacilities) {
                  return null;
                }

                return (
                  image && (
                    <Grid key={type} className={classes.modalGridItem} align='center' size={2}>
                      <img src={image} width='100px' height='65px' alt={`${name}-icon`} />
                      <Typography variant='h6' style={{ margin: '15px 0' }}>
                        {name}
                      </Typography>
                      <Grid
                        container
                        spacing={1}
                        className={classes.modalButtons}
                        sx={{
                          justifyContent: 'center',
                        }}
                      >
                        {regions.map(({ displayName, region, type: subType }) => (
                          <Grid key={`data-downloads-${type}-${displayName}`}>
                            <Tooltip
                              title={
                                !userEntitledRegions?.includes(displayName)
                                  ? 'Your current plan does not include access to this file.'
                                  : ''
                              }
                              disableHoverListener={!!userEntitledRegions?.includes(displayName)}
                              arrow
                            >
                              <span>
                                <Button
                                  key={type + displayName}
                                  variant='contained'
                                  className={classes.centeredButton}
                                  disabled={!userEntitledRegions?.includes(displayName)}
                                  onClick={() => {
                                    if (!userEntitledRegions?.includes(displayName)) return;
                                    gtag('event', ANALYTICS_EVENTS.event, {
                                      event_category: 'IndustryTrackers',
                                      event_action: ANALYTICS_EVENT_ACTIONS.download,
                                      event_label: `${name} | ${displayName}`,
                                      userId: getUserId(),
                                      ...defaultDimensions,
                                    });
                                    getDownloadLink(type, region, subType);
                                  }}
                                >
                                  {displayName || 'Download Data'}
                                </Button>
                              </span>
                            </Tooltip>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )
                );
              })}
            </Grid>
          </Modal>
        </Grid>
        {globalDownloads && (
          <Grid>
            <Button
              component={Link}
              to={GLOBAL_DOWNLOADS_PATH}
              variant='contained'
              className={classes.button}
              underline='none'
              disabled={!isUserEntitled(GLOBAL_DOWNLOADS_PATH)}
            >
              <GetAppIcon fontSize='small' />
              &nbsp;global downloads
            </Button>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
